'use client'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/app/utils/style'

interface AICard {
  title: string
  description: string
  badge?: string
}

interface Conversation {
  userMessage: string
  assistantMessage: string
  cards?: AICard[]
}

const conversations: Conversation[] = [
  {
    userMessage:
      'Find me technology newsletters with at least 50,000 subscribers that I can sponsor.',
    assistantMessage:
      'I found 12 technology newsletters with 50,000+ subscribers available for sponsorship. Here are the top matches:',
    cards: [
      {
        title: 'The Byte',
        description: '120K subscribers, dedicated sponsorship starting at $2,500/issue',
      },
      {
        title: 'DevWeekly',
        description: '85K subscribers, classified ad from $800/issue',
      },
      {
        title: 'CloudBrief',
        description: '67K subscribers, dedicated sponsorship starting at $1,800/issue',
      },
    ],
  },
  {
    userMessage: "What's the status of my active campaigns?",
    assistantMessage: 'You have 3 active campaigns:',
    cards: [
      {
        title: 'The Byte',
        description:
          'Apr 14 • Awaiting creative. You still need to submit a headline and a 300x250 banner image.',
        badge: 'Approved',
      },
      {
        title: 'DevWeekly',
        description: 'Apr 21 • Pending publisher response.',
        badge: 'Requested',
      },
      {
        title: 'Founder Digest',
        description: 'Apr 7 • All requirements met. Ready to go!',
        badge: 'Creative submitted',
      },
    ],
  },
  {
    userMessage: 'I want to sponsor The Byte on April 22nd.',
    assistantMessage:
      "Let me check on that. First, your account is eligible and billing is set up — you're good to go. However, April 22nd is unavailable for The Byte's dedicated sponsorship slot. The nearest open dates are April 28th ($2,500) and May 1st ($2,200).\nWant me to request the sponsorship on one of those dates? I can also include a message to the publisher if you'd like.",
    cards: [],
  },
]

interface MCPChatMessagesProps {
  className?: string
}

export default function MCPChatMessages({ className }: MCPChatMessagesProps) {
  const [currentConvIndex, setCurrentConvIndex] = useState(0)
  const [userTypedText, setUserTypedText] = useState('')
  const [showAssistant, setShowAssistant] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const assistantTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cycleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const currentConv = conversations[currentConvIndex]
    const userMsg = currentConv.userMessage
    let charIndex = 0

    setUserTypedText('')
    setShowAssistant(false)
    setIsTransitioning(false)
    setIsTyping(true)

    const typeNextChar = () => {
      if (charIndex < userMsg.length) {
        setUserTypedText(userMsg.substring(0, charIndex + 1))
        charIndex++
        typingTimeoutRef.current = setTimeout(typeNextChar, 30)
      } else {
        setIsTyping(false)
        assistantTimeoutRef.current = setTimeout(() => {
          setShowAssistant(true)
        }, 800)

        cycleTimeoutRef.current = setTimeout(() => {
          setShowAssistant(false)
          setIsTransitioning(true)
          setTimeout(() => {
            setCurrentConvIndex((prev) => (prev + 1) % conversations.length)
          }, 400)
        }, 6000)
      }
    }

    typeNextChar()

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (assistantTimeoutRef.current) clearTimeout(assistantTimeoutRef.current)
      if (cycleTimeoutRef.current) clearTimeout(cycleTimeoutRef.current)
    }
  }, [currentConvIndex])

  const currentConv = conversations[currentConvIndex]

  return (
    <div
      className={cn('relative w-full max-w-[548px] rounded-[20px] overflow-hidden', className)}
      style={{
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Header with dots */}
      <div
        className="flex items-center gap-[6px]"
        style={{
          padding: '17px 20px',
          borderBottom: '1px solid #E3E7EE',
        }}
      >
        <div
          className="w-[12px] h-[12px] rounded-full"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
        />
        <div
          className="w-[12px] h-[12px] rounded-full"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
        />
        <div
          className="w-[12px] h-[12px] rounded-full"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
        />
      </div>

      {/* Messages container */}
      <div className="relative min-h-[600px] p-[24px] flex flex-col gap-[12px]">
        {/* User message */}
        <div className="flex justify-end">
          <div
            className="relative flex items-end"
            style={{
              width: '344px',
              minHeight: '69px',
            }}
          >
            {/* Transparent element to predetermine height */}
            <div
              className="opacity-0 rounded-[10px]"
              style={{
                padding: '12px',
                fontSize: '14px',
                lineHeight: '22.4px',
              }}
            >
              {currentConv.userMessage}
            </div>
            {/* Blue bubble positioned absolutely at bottom */}
            <div
              className="absolute bottom-0 left-0 right-0 rounded-[10px]"
              style={{
                backgroundColor: '#4586F6',
                color: '#FFFFFF',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '22.4px',
              }}
            >
              {userTypedText}
              {isTyping && (
                <span
                  className="inline-block ml-[2px] animate-[blink_1s_infinite]"
                  style={{
                    animation: 'mcp-cursor-blink 1s infinite',
                  }}
                >
                  |
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Assistant message */}
        <div
          className={cn(
            'flex justify-start transition-all duration-[450ms]',
            showAssistant ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[10px]',
          )}
        >
          <div
            className="rounded-[10px] flex flex-col"
            style={{
              backgroundColor: '#F8FAFF',
              color: '#353C43',
              padding: '12px',
              fontSize: '14px',
              lineHeight: '22.4px',
              gap: '10px',
              maxWidth: '331px',
            }}
          >
            <div>{currentConv.assistantMessage}</div>

            {/* AI cards */}
            {currentConv.cards && currentConv.cards.length > 0 && (
              <div className="flex flex-col gap-[8px]">
                {currentConv.cards.map((card, idx) => (
                  <div
                    key={idx}
                    className="rounded-[16px] flex flex-col gap-[4px] relative"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E3E7EE',
                      padding: '12px',
                    }}
                  >
                    <div
                      className="font-medium"
                      style={{
                        color: '#4586F6',
                        fontSize: '14px',
                        lineHeight: '22.4px',
                      }}
                    >
                      {card.title}
                    </div>
                    <div
                      style={{
                        color: '#353C43',
                        fontSize: '12px',
                        lineHeight: '19.2px',
                      }}
                    >
                      {card.description}
                    </div>
                    {card.badge && (
                      <div
                        className="absolute top-[12px] right-[12px] rounded-full font-medium"
                        style={{
                          backgroundColor: 'rgba(44, 210, 128, 0.31)',
                          color: '#0DA058',
                          fontSize: '12px',
                          lineHeight: '19.2px',
                          padding: '2px 8px',
                        }}
                      >
                        {card.badge}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {currentConvIndex === 0 && (
              <div>
                Want me to pull up the full profile for any of these, check date availability, or
                find similar newsletters?
              </div>
            )}
          </div>
        </div>

        {/* Overlay for transitions */}
        <div
          className={cn(
            'absolute inset-0 pointer-events-none transition-opacity duration-[400ms] z-10',
            isTransitioning ? 'opacity-100' : 'opacity-0',
          )}
          style={{ backgroundColor: '#FFFFFF' }}
        />
      </div>
    </div>
  )
}
